import * as React from 'react';

import {
    DeleteOutlined,
    EyeOutlined,
    FileUnknownOutlined,
    FrownOutlined,
    LoadingOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';

import { message, Modal } from 'antd';
import { get, upload } from 'pc-sales/utils/http';
import utils from 'common/utils/upload';
import languages from 'common/i18n/utils/languages';
import * as intl from "react-intl-universal";
import PDF from 'common/components/PDF';
import RcViewer from "@hanyk/rc-viewer";

type statusType = 'upload' | 'loading' | 'preview' | 'error';

interface iState {
    status: statusType;
    previewVisible: boolean;
    previewImage: string;
    defaultImage: string;
    errorMsg: string;
    encryptUrl?: string;
    isPdf: boolean;
}

export default class Uploader extends React.Component<any> {

    constructor(props: any) {
        super(props);
        const lang = languages.getDefault();
        this.locales = utils.getLocales(lang);
    }

    /**
     * 暂不支持多张图片的上传
     */
    static defaultProps = {
        action: '/',
        max: 1
    };
    private uploadCore: any;
    locales: any = {};
    cache: any = {
        url: '',
        imageId: ''
    };
    mounted: boolean = null;
    state: iState = {
        status: 'loading',
        previewVisible: false,
        previewImage: '',
        defaultImage: '',
        errorMsg: '',
        isPdf: false
    };

    componentDidMount() {
        this.mounted = true;
        const { value } = this.props;
        this.init(value);
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    UNSAFE_componentWillReceiveProps(nextProps: any) {
        if (nextProps.value !== this.props.value) {
            this.mounted && this.init(nextProps.value);
        }
    }

    shouldComponentUpdate(nextProps: any, nextState: any) {
        if (nextProps.value === this.props.value
            && nextProps.disabled === this.props.disabled
            && JSON.stringify(nextState) === JSON.stringify(this.state)) return false;
        return true;
    }

    init = (value: any) => {
        const { encryptUrl = '/v2/common/file/decryptImage' } = this.props;
        if (!!value && typeof value === 'string') {
            get(encryptUrl, { id: value }, { ignoreError: true }).then(({ url }: any) => {
                this.cache = { imageId: value, url };
                const isPdf = url.split('.').pop() === 'pdf';
                this.mounted && this.setState({
                    previewImage: url,
                    status: 'preview',
                    defaultImage: url,
                    isPdf
                });
            }).catch(() => {
                this.mounted && this.setState({
                    status: 'error',
                    errorMsg: this.locales.error
                });
            });
        } else {
            this.mounted && this.setState({
                status: 'upload',
                previewVisible: false,
                previewImage: '',
                defaultImage: '',
                errorMsg: '',
                isPdf: false
            });
        }
    };

    /**
     * 取消预览
     * @param e
     */
    cancelHandler = (e: any) => {
        e.stopPropagation();
        this.setState({ previewVisible: false });
        return false;
    };

    /**
     * 删除图片
     * @param e
     */
    removeHandler = (e: any) => {
        e.stopPropagation();
        const { onChange } = this.props;
        // 移除input组件的值 避免无法监听 选择相同文件的 onChange 事件 导致组件显示异常
        this.uploadCore.value = '';
        this.setState({
            isPdf: false,
            previewImage: '',
            status: 'upload',
            errorMsg: ''
        });
        onChange && onChange(null);
        return false;
    };

    /**
     * 打开预览
     */
    previewHandler = () => {
        const { isPdf } = this.state;
        if (isPdf) {
            return this.setState({
                previewVisible: true,
            });
        }

        const { viewer } = this.viewer.getViewer();

        viewer.view(0); // View the n image
    };

    /**
     * 图片加密处理
     */
    private encryptImage = async (file: any, src: string) => {
        try {
            const { action, onChange } = this.props;
            const { imageId }: any = await upload(action, { file });
            onChange && onChange(imageId);
            this.setState({ status: 'preview', previewImage: src, errorMsg: '' });
        } catch (err) {
            this.setState({ status: 'error', previewImage: '', errorMsg: this.locales.error });
            throw err;
        }
    };

    /**
     * 文件流处理
     */
    private fileChangeHandler = (file: any) => {
        const quality = this.getQuality(file);
        if (this.uploadCore && quality) {
            const core = new FileReader();
            core.readAsDataURL(file);
            core.onload = (e: any) => {
                if (quality === 1) this.encryptImage(file, e.target.result).catch();
                else this.imgPressHandler(e.target.result, quality, core);
            };
        } else {
            this.setState({ previewImage: this.cache.url, status: 'preview' });
        }
    };

    /**
     * 获取压缩参数
     * @param file
     */
    getQuality = (file: any): any => {
        if (file.type.split('/')[0] !== 'image' && file.type.split('/')[1] !== 'pdf') {
            message.error(this.locales.type);
            return;
        }
        const mb = 1024 * 1024;
        if (file.size > 30 * mb) {
            message.error(this.locales.size);
            return;
        }
        // 根据图片大小设置压缩系数
        if (Math.max(0, file.size, 3 * mb) == (3 * mb)) {
            return 1;
        } else if (Math.max(3 * mb, file.size, 15 * mb) == (15 * mb)) {
            return 0.6;
        } else if (Math.max(15 * mb, file.size, 32 * mb) == (32 * mb)) {
            return 0.3;
        }
    };

    /**
     * 图像压缩
     * @param src
     * @param quality
     * @param file
     */
    imgPressHandler = (src: string, quality: number, file: any) => {
        utils.imagePress({ file, quality, src, type: 'blob' }, (blob: any, src: any) => {
            blob && this.encryptImage(blob, src).catch();
        });
    };

    /**
     * 下载
     */
    downloadHandler = () => {
        const { previewImage } = this.state;
        const aTag = document.createElement('a');
        aTag.href = `/v2/common/file/download?uri=${previewImage}`;
        aTag.click();
    };

    /**
     * 文件上传点击控制
     */
    openUploadHandler = () => {
        if ((this.uploadCore && !this.state.previewImage) || (this.state.status === 'error' && this.state.previewImage)) {
            if (this.state.status === 'error' && this.state.previewImage) {
                this.setState({
                    previewImage: ''
                });
            }
            this.uploadCore.click();
            this.uploadCore.onchange = () => {
                const file = this.uploadCore.files[0];
                if (file) {
                    this.setState({
                        isPdf: file.type.split('/')[1] === 'pdf',
                        status: 'loading',
                        errorMsg: ''
                    });
                    this.fileChangeHandler(file);
                }
            };
        }
    };

    /**
     * 组件状态切换
     * @param {statusType} status
     * @param extra
     */
    statusChangeHandler = (status: statusType, extra?: any) => () => {
        this.setState({ status, ...extra });
    };

    private viewer: any;

    fnRef = (el: any) => this.viewer = el;

    render() {
        const { accept, disabled: propsDisabled, tips, readOnly } = this.props;
        const { previewVisible, previewImage, status, errorMsg, isPdf } = this.state;
        const disabled = propsDisabled || readOnly;
        return (
            <div className="clearfix">
                <div className='Uploader'>
                    <span className="Uploader-btn" role="button" onClick={!disabled ? this.openUploadHandler : null}>
                        <input type="file" accept={accept} ref={(ctx: any) => this.uploadCore = ctx}
                               style={{ display: "none" }}/>
                        <div>{({
                            upload: !disabled ? <span key='upload'>
                                <PlusOutlined />
                                <div className="Uploader-text">{this.locales.upload}</div>
                            </span> : <span key='upload'>
                                    <FileUnknownOutlined />
                                    <div className="Uploader-text">{this.locales.unUpload}</div>
                                </span>,
                            loading: <span key='loading'>
                                <LoadingOutlined />
                                <div className="Uploader-text">{History.loading}</div>
                            </span>,
                            preview: <div key='preview' className='Uploader-hover'>
                                {isPdf ? <PDF src={previewImage} hideButton={true} overlayStyle={{
                                        height: '94px',
                                        overflowY: 'hidden',
                                        overflowX: 'hidden'
                                    }}/> :
                                    <img src={previewImage} style={{ width: '100%', height: '100%', padding: '8px' }}
                                         onError={this.statusChangeHandler('error')} alt={''}/>}
                                <span className="Uploader-float">
                                    <a onClick={this.previewHandler}><EyeOutlined /></a>
                                    {!disabled && <a onClick={this.removeHandler}><DeleteOutlined /></a>}
                                </span>
                            </div>,
                            error: <div key='error' className='Uploader-hover'>
                                <span className="Uploader-error">
                                    <a>
                                        <FrownOutlined />
                                        <div className="Uploader-text">{errorMsg}</div>
                                    </a>
                                </span>
                                {!disabled && <span className="Uploader-float">
                                    <a onClick={this.statusChangeHandler('upload', { errorMsg: '' })}>
                                        <ReloadOutlined />
                                        <div className="Uploader-text">{this.locales.reload}</div>
                                    </a>
                                </span>}
                            </div>
                        } as any)[status]}</div>
                    </span>
                </div>
                {tips && <p style={{ fontSize: '12px', color: "rgb(148, 144, 144)" }}>{tips}</p>}
                {
                    isPdf && <Modal title={intl.get('Global.CheckImage')}
                                    width={800}
                                    visible={previewVisible}
                                    onOk={this.downloadHandler}
                                    onCancel={this.cancelHandler}
                                    okText={intl.get('Global.Download')}
                                    maskClosable={false}>
                        <PDF src={previewImage} hideButton={true} overlayStyle={{
                            maxHeight: '380px',
                            maxWidth: '100%',
                            overflowY: 'auto',
                            overflowX: 'auto'
                        }}/>
                    </Modal>
                }
                {
                    !isPdf && <RcViewer ref={this.fnRef}>
                        <img style={{ display: 'none' }}
                            src={previewImage} alt=""/>
                    </RcViewer>
                }
            </div>
        );
    }
}
